'use client';

import React from 'react';
import { Check } from 'lucide-react';
import { AiSkillPublic } from '@ctrl-enter/types/ai-skills';
import { cn } from '../../utils/cn';
import { Icon, type IconProps } from '../icons/icon';

type SkillCardProps = {
  skill: AiSkillPublic;
  isSelected: boolean;
  onSelect: (skill: AiSkillPublic) => void;
};

export const SkillCard: React.FC<SkillCardProps> = ({ skill, isSelected, onSelect }) => {
  return (
    <button
      type="button"
      onClick={() => onSelect(skill)}
      aria-pressed={isSelected}
      className={cn(
        'relative text-left w-full rounded-xl p-4 transition-all duration-200',
        'border border-transparent',
        'bg-gradient-to-br',
        skill.accentColor,
        'hover:border-primary/30 hover:shadow-md',
        isSelected && 'border-primary/50 ring-2 ring-primary/20 shadow-md'
      )}
    >
      {/* Selected indicator */}
      {isSelected && (
        <span className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground">
          <Check size={12} strokeWidth={3} />
        </span>
      )}

      {/* Icon */}
      <div
        className={cn(
          'flex items-center justify-center w-9 h-9 rounded-lg mb-3',
          'bg-background/60 backdrop-blur-sm',
          isSelected ? 'text-primary' : 'text-text-secondary'
        )}
      >
        <Icon iconType={skill.icon as IconProps['iconType']} size="md" />
      </div>

      {/* Name */}
      <h3 className="text-sm font-semibold text-foreground leading-tight mb-1">{skill.name}</h3>

      {/* Tagline */}
      <p className="text-xs text-text-secondary leading-snug line-clamp-2">{skill.tagline}</p>
    </button>
  );
};
